import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { Vehicle } from '@features/vehiclesearch/types/vinvrmsearch.types';
import type { VehicleDerivative } from '@features/vehiclesearch/types/manualsearch.types';
import type { DateKey } from '@features/quote/utils/dateKey';
import { deriveVehicleScheduleContext } from '@/app/utils/vehicleScheduleContext';
import { fetchDerivedSchedule } from '../services/derivedSchedule';
import type { DerivedScheduleResponse } from '../types/derivedSchedule.types';
import { scheduleOverridesKey } from '../utils/scheduleOverridesKey';

export const derivedScheduleKeys = {
    all: ['derivedSchedule'] as const,
    detail: (
        productId: string,
        planId: string | null,
        count: number | null,
        vehicleAgeYears: number | null,
        firstRegistrationDate: string | null,
        vehicleMileage: number | null,
        annualMileage: number | null,
        lastServiceDate: string | null,
        lastServiceMileage: number | null,
        // Identity affects which plan resolves (11.3.2), so it must be part of the key.
        makeId: string | null = null,
        modelId: string | null = null,
        derivativeId: string | null = null,
        transmissionId: string | null = null,
        fuelTypeId: string | null = null,
        engineSize: number | null = null,
        is4x4: boolean | null = null,
        startEntryIndex: number | null = null,
        // `dateOverridesKey` is `scheduleOverridesKey(dateOverrides)` —
        // sorted and stable, so key-insertion order never causes a cache miss.
        dateOverridesKey: string = ''
    ) =>
        [
            ...derivedScheduleKeys.all,
            productId,
            planId,
            count,
            vehicleAgeYears,
            firstRegistrationDate,
            vehicleMileage,
            annualMileage,
            lastServiceDate,
            lastServiceMileage,
            makeId,
            modelId,
            derivativeId,
            transmissionId,
            fuelTypeId,
            engineSize,
            is4x4,
            startEntryIndex,
            dateOverridesKey,
        ] as const,
};

export interface UseDerivedScheduleArgs {
    productId?: string | null;
    planId?: string | null;
    /**
     * Number of authoritative entries to request (Slice A.1). Guided mode
     * passes `bounds.max` so "Add" can reveal successive already-fetched
     * entries; locked mode omits it (the server returns `bounds.default`).
     */
    count?: number | null;
    vehicle?: Vehicle | VehicleDerivative | null;
    // Epic 2.4.6: undefined/null and an empty map for the whole of slice 4a —
    // no UI in this slice can set either.
    startEntryIndex?: number | null;
    dateOverrides?: Record<number, DateKey>;
}

// The full request identity of a derive: every dimension that selects a
// response, including the matcher-only fields `derivationFingerprint` omits.
export const derivedScheduleQueryKey = ({
    productId,
    planId,
    count,
    vehicle,
    startEntryIndex,
    dateOverrides,
}: UseDerivedScheduleArgs) => {
    const vehicleContext = deriveVehicleScheduleContext(vehicle);

    return derivedScheduleKeys.detail(
        productId ?? '',
        planId ?? null,
        count ?? null,
        vehicleContext.vehicleAgeYears ?? null,
        vehicleContext.firstRegistrationDate ?? null,
        vehicleContext.vehicleMileage ?? null,
        vehicleContext.annualMileage ?? null,
        vehicleContext.lastServiceDate ?? null,
        vehicleContext.lastServiceMileage ?? null,
        vehicleContext.makeId ?? null,
        vehicleContext.modelId ?? null,
        vehicleContext.derivativeId ?? null,
        vehicleContext.transmissionId ?? null,
        vehicleContext.fuelTypeId ?? null,
        vehicleContext.engineSize ?? null,
        vehicleContext.is4x4 ?? null,
        startEntryIndex ?? null,
        scheduleOverridesKey(dateOverrides)
    );
};

/**
 * React Query wrapper around GET smr/products/{product}/derived-schedule.
 * Every product now derives, so this fires once a product is selected.
 */
export const useDerivedSchedule = ({
    productId,
    planId,
    count,
    vehicle,
    startEntryIndex,
    dateOverrides,
}: UseDerivedScheduleArgs): UseQueryResult<DerivedScheduleResponse> => {
    const vehicleContext = deriveVehicleScheduleContext(vehicle);
    const enabled = !!productId;

    return useQuery<DerivedScheduleResponse>({
        queryKey: derivedScheduleQueryKey({
            productId,
            planId,
            count,
            vehicle,
            startEntryIndex,
            dateOverrides,
        }),
        queryFn: () =>
            fetchDerivedSchedule(productId as string, {
                vehicleAgeYears: vehicleContext.vehicleAgeYears,
                firstRegistrationDate: vehicleContext.firstRegistrationDate,
                vehicleMileage: vehicleContext.vehicleMileage,
                annualMileage: vehicleContext.annualMileage,
                lastServiceDate: vehicleContext.lastServiceDate,
                lastServiceMileage: vehicleContext.lastServiceMileage,
                planId: planId ?? undefined,
                count: count ?? undefined,
                makeId: vehicleContext.makeId,
                modelId: vehicleContext.modelId,
                derivativeId: vehicleContext.derivativeId,
                transmissionId: vehicleContext.transmissionId,
                fuelTypeId: vehicleContext.fuelTypeId,
                engineSize: vehicleContext.engineSize,
                is4x4: vehicleContext.is4x4,
                startEntryIndex,
                dateOverrides,
            }),
        enabled,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });
};
